import React from 'react';
import { AlertTriangle, ChevronRight } from 'lucide-react';
import { validityLabel } from './plannerLabels';

/**
 * The plan, end to end, as a strip of chips.
 *
 * <p>This is the part of the feature that answers the question the whole thing exists for
 * - "if I use this team this week, what does the next month look like?" - because a route
 * is only legible all at once. Five separate gameweek cards can each be perfectly clear
 * and still leave a player unable to say whether they are about to run out of teams.
 *
 * <p>Horizontal on a phone rather than a vertical list with arrows between: five chips
 * scroll in a thumb-width, and the vertical version pushes the fixtures - the thing the
 * player came to look at - below the fold.
 *
 * <p>Every chip is a button that jumps to that gameweek's fixtures, which is the only
 * navigation the route needs: reading the plan and wanting to change one leg of it are
 * the same moment.
 */
export default function PlannedRoute({ weeks = [], routeByWeek = {}, activeWeek, onSelectWeek }) {
  if (weeks.length === 0) return null;

  const plannedCount = weeks.filter((w) => routeByWeek[w.gameWeek]).length;
  const problemCount = weeks.filter((w) => {
    const planned = routeByWeek[w.gameWeek];
    return planned && planned.validity !== 'VALID';
  }).length;

  return (
    <section className="pl-route" aria-labelledby="pl-route-heading">
      <div className="pl-route-head">
        {/* "Your planned route" rather than "Your plan": the team chips below call the
            current week's own entry "Your plan" too, and one screen cannot use the same
            phrase for the whole route and for one leg of it. */}
        <h3 id="pl-route-heading" className="pl-route-title">Your planned route</h3>
        <p className="pl-route-count">
          {plannedCount === 0
            ? 'Nothing planned yet'
            : `${plannedCount} of ${weeks.length} gameweeks planned`}
          {problemCount > 0 && (
            <span className="pl-route-problems">
              {' · '}
              {problemCount} {problemCount === 1 ? 'needs' : 'need'} replanning
            </span>
          )}
        </p>
      </div>

      {/* A list rather than a row of buttons: a screen reader should be able to walk the
          route and be told how long it is, which "5 buttons" does not say. */}
      {/* role="list" is redundant markup that is not redundant in practice: VoiceOver in
          Safari drops list semantics from a list styled with list-style: none, and the
          number of legs in the route is the thing this element exists to convey. */}
      {/* eslint-disable-next-line jsx-a11y/no-redundant-roles -- see above */}
      <ol className="pl-route-strip" role="list">
        {weeks.map((week, index) => {
          const planned = routeByWeek[week.gameWeek];
          const problem = planned ? validityLabel(planned.validity) : null;
          const isActive = week.gameWeek === activeWeek;
          // A confirmed pick takes the chip over whatever was planned for that week.
          // Reading the strip and being told "not planned" for the week you have already
          // picked is the route contradicting the card underneath it, and the pick is the
          // one leg of the route that is no longer a question.
          const confirmed = week.officialPick;

          return (
            <li key={week.gameWeek} className="pl-route-item">
              <button
                type="button"
                className={[
                  'pl-chip',
                  isActive ? 'is-active' : '',
                  confirmed ? 'is-confirmed' : (planned ? 'is-planned' : 'is-empty'),
                  problem && !confirmed ? 'is-invalid' : '',
                ].filter(Boolean).join(' ')}
                aria-current={isActive ? 'true' : undefined}
                aria-label={`Gameweek ${week.gameWeek}: `
                  + (confirmed
                    ? `${confirmed.teamName}, confirmed pick`
                    : (planned ? planned.teamName : 'not planned'))
                  + (problem && !confirmed ? ` — ${problem}` : '')}
                onClick={() => onSelectWeek?.(week.gameWeek)}
              >
                <span className="pl-chip-week">GW{week.gameWeek}</span>
                <span className="pl-chip-team">
                  {confirmed ? confirmed.teamName : (planned ? planned.teamName : 'Not planned')}
                </span>
                {confirmed && <span className="pl-chip-confirmed">Picked</span>}
                {problem && !confirmed && (
                  <span className="pl-chip-problem">
                    <AlertTriangle size={12} aria-hidden="true" /> {problem}
                  </span>
                )}
              </button>
              {index < weeks.length - 1 && (
                <ChevronRight size={16} className="pl-route-arrow" aria-hidden="true" />
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
